import {
  ArrowLeft,
  ExternalLink,
  FolderOpen,
  Pencil,
  Upload,
} from "lucide-react";
import { useCanAccess } from "ra-core";
import { useState } from "react";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import { KIND_ICON } from "./kindIcon";
import { EditMaterialDialog, UploadMaterialDialog } from "./MaterialDialogs";
import { formatSize, groupMaterials, type Material } from "./materials";
import { useMaterials } from "./useMaterials";

const MaterialRow = ({
  material,
  onEdit,
}: {
  material: Material;
  onEdit?: () => void;
}) => {
  const Icon = KIND_ICON[material.kind];
  return (
    <li className="flex items-center gap-3 border-b px-4 py-3 last:border-b-0">
      <Icon className="h-5 w-5 shrink-0 text-muted-foreground" />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="flex items-center gap-2 text-sm font-medium">
          <span className="truncate">{material.title}</span>
          {!material.active ? <Badge variant="secondary">Oculto</Badge> : null}
        </span>
        {material.note ? (
          <span className="text-xs text-muted-foreground">{material.note}</span>
        ) : null}
        <span className="truncate text-[11px] text-muted-foreground">
          {material.file_name} · {formatSize(material.size_bytes)}
        </span>
      </div>
      <Button asChild size="icon" variant="ghost" title="Ver">
        <a href={material.url} target="_blank" rel="noreferrer">
          <ExternalLink className="h-4 w-4" />
        </a>
      </Button>
      {onEdit ? (
        <Button size="icon" variant="ghost" title="Editar" onClick={onEdit}>
          <Pencil className="h-4 w-4" />
        </Button>
      ) : null}
    </li>
  );
};

/** The library of files the setters send by WhatsApp; admins manage it. */
export const MaterialsPage = () => {
  const { materials, isPending, refetch } = useMaterials();
  const { canAccess: isAdmin = false } = useCanAccess({
    resource: "money",
    action: "list",
  });
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState<Material | null>(null);
  const groups = groupMaterials(materials);
  const hidden = materials.filter((material) => !material.active);

  return (
    <div className="flex flex-col gap-4 py-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link
            to="/whatsapp"
            className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            WhatsApp
          </Link>
          <h1 className="flex items-center gap-2 text-3xl font-semibold tracking-tight">
            <FolderOpen className="h-7 w-7" />
            Material
          </h1>
          <p className="text-sm text-muted-foreground">
            Lo que se envía a los clientes desde la conversación de WhatsApp
            (botón de la carpeta).
          </p>
        </div>
        {isAdmin ? (
          <Button onClick={() => setUploading(true)}>
            <Upload className="h-4 w-4" />
            Subir material
          </Button>
        ) : null}
      </div>

      {isPending ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : !materials.length ? (
        <p className="rounded-2xl border p-6 text-sm text-muted-foreground">
          Todavía no hay material.
          {isAdmin ? " Sube vídeos, precios, la ficha técnica y fotos." : ""}
        </p>
      ) : null}

      {groups.map((group) => (
        <section key={group.value} className="flex flex-col gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {group.label}
          </h2>
          <ul className="overflow-hidden rounded-2xl border bg-card">
            {group.items.map((material) => (
              <MaterialRow
                key={material.id}
                material={material}
                onEdit={isAdmin ? () => setEditing(material) : undefined}
              />
            ))}
          </ul>
        </section>
      ))}

      {isAdmin && hidden.length ? (
        <section className="flex flex-col gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Ocultos
          </h2>
          <ul className="overflow-hidden rounded-2xl border bg-card">
            {hidden.map((material) => (
              <MaterialRow
                key={material.id}
                material={material}
                onEdit={() => setEditing(material)}
              />
            ))}
          </ul>
        </section>
      ) : null}

      <UploadMaterialDialog
        open={uploading}
        onOpenChange={setUploading}
        onSaved={() => refetch()}
      />
      <EditMaterialDialog
        material={editing}
        onOpenChange={(open) => !open && setEditing(null)}
        onSaved={() => refetch()}
      />
    </div>
  );
};

MaterialsPage.path = "/whatsapp/material";
